import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { metaAddToCart } from "@/lib/metaPixel";
import { openaiAddToCart } from "@/lib/openaiPixel";
import { api, API } from "@/lib/api";

const CartContext = createContext(null);
const STORAGE_KEY = "luxsova_cart_v1";
const COUPON_KEY = "luxsova_coupon_v1";
// One-time migration: bags saved under the old storage names are carried over, then the old names are removed.
const _OLD = ["lux", "maart"].join("");
const LEGACY_CART_KEY = `${_OLD}_cart_v1`;
const LEGACY_COUPON_KEY = `${_OLD}_coupon_v1`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(LEGACY_CART_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      console.warn("[cart] read failed:", e);
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const [coupon, setCouponState] = useState(() => {
    try {
      const raw = localStorage.getItem(COUPON_KEY) ?? localStorage.getItem(LEGACY_COUPON_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      localStorage.removeItem(LEGACY_CART_KEY);
    } catch (e) {
      console.warn("[cart] persist failed:", e);
    }
  }, [items]);

  useEffect(() => {
    try {
      if (coupon) localStorage.setItem(COUPON_KEY, JSON.stringify(coupon));
      else localStorage.removeItem(COUPON_KEY);
      localStorage.removeItem(LEGACY_COUPON_KEY);
    } catch {
      /* ignore quota errors */
    }
  }, [coupon]);

  const setCoupon = (c) => setCouponState(c);
  const clearCoupon = () => setCouponState(null);

  /** Replace the cart contents in one shot — used by the resume-cart flow. */
  const replaceItems = (newItems) => {
    setItems(Array.isArray(newItems) ? newItems : []);
    setOpen(true);
  };

  const addItem = (product, variation = null, quantity = 1) => {
    const key = `${product.id}-${variation?.id || "default"}`;
    setItems((prev) => {
      const existing = prev.find((p) => p.key === key);
      if (existing) {
        return prev.map((p) =>
          p.key === key ? { ...p, quantity: p.quantity + quantity } : p,
        );
      }
      return [
        ...prev,
        {
          key,
          id: product.id,
          slug: product.slug,
          name: product.name,
          image: product.image || product.images?.[0],
          price: variation?.price || product.price,
          variationId: variation?.id || null,
          variationLabel: variation?.label || null,
          quantity,
          url:
            typeof window !== "undefined"
              ? `${window.location.origin}/product/${product.slug}`
              : undefined,
        },
      ];
    });
    // Stay on the page: confirm with a small toast (with a "View bag" shortcut)
    // instead of opening the full cart drawer.
    toast.success("Added to bag", {
      description: product.name,
      duration: 3000,
      action: { label: "View bag", onClick: () => setOpen(true) },
    });
    // Meta AddToCart (Pixel + CAPI)
    const atcPayload = {
      id: product.id,
      slug: product.slug,
      name: product.name,
      price: variation?.price || product.price,
      quantity,
    };
    metaAddToCart(atcPayload);
    // OpenAI ChatGPT Ads AddToCart (oaiq + CAPI)
    openaiAddToCart(atcPayload);
    // Popularity signal — bumps this product in the "Popular" sort.
    api.trackEngagement(product.slug, "atc");
  };

  const removeItem = (key) =>
    setItems((prev) => prev.filter((p) => p.key !== key));
  const updateQty = (key, quantity) =>
    setItems((prev) =>
      prev.map((p) =>
        p.key === key ? { ...p, quantity: Math.max(1, quantity) } : p,
      ),
    );
  const clear = () => {
    setItems([]);
    setCouponState(null);
  };

  // Keep bag prices in sync with the live catalogue. A bag remembers the price
  // from the day an item was added; when the price has changed since, the
  // customer must see the new price in the bag — not first on WhatsApp.
  const lastPriceRefresh = useRef(0);
  const refreshPrices = async (force = false) => {
    if (!items.length) return;
    const now = Date.now();
    if (!force && now - lastPriceRefresh.current < 20000) return;
    lastPriceRefresh.current = now;
    try {
      const res = await fetch(`${API}/cart/prices`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: items.map((it) => ({
            name: it.name,
            quantity: it.quantity,
            price: it.price,
            variation: it.variationLabel || undefined,
            url: it.url,
            slug: it.slug,
          })),
        }),
      });
      if (!res.ok) return;
      const data = await res.json();
      const live = data.prices || [];
      const byKey = {};
      items.forEach((it, i) => {
        byKey[it.key] = live[i];
      });
      const differs = (it) => {
        const p = parseFloat(byKey[it.key]);
        return p > 0 && p !== parseFloat(it.price);
      };
      if (items.some(differs)) {
        setItems((prev) =>
          prev.map((it) => (differs(it) ? { ...it, price: String(byKey[it.key]) } : it)),
        );
        toast.info("Some prices in your bag were updated to today's prices.");
      }
    } catch (e) {
      /* offline — keep what we have */
    }
  };

  useEffect(() => {
    refreshPrices(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (open) refreshPrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshPrices();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const subtotal = useMemo(
    () =>
      items.reduce(
        (s, it) => s + (parseFloat(it.price) || 0) * it.quantity,
        0,
      ),
    [items],
  );
  const count = useMemo(
    () => items.reduce((s, it) => s + it.quantity, 0),
    [items],
  );

  return (
    <CartContext.Provider
      value={useMemo(
        () => ({
          items,
          addItem,
          removeItem,
          updateQty,
          clear,
          replaceItems,
          refreshPrices,
          subtotal,
          count,
          open,
          setOpen,
          coupon,
          setCoupon,
          clearCoupon,
        }),
        // addItem/removeItem/updateQty/clear are closures recreated each
        // render — but they only close over setItems (stable) and setOpen
        // (stable), so re-creating them is cheap. We rebuild the memo when
        // any consumed value changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [items, subtotal, count, open, coupon],
      )}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
